import { z } from 'zod';
import { isoDateTimeSchema, uuidSchema } from './common.js';
/**
 * Schedule slot wire shape (admin-arch §3.7.1 + ADR-005 `loop-until-next`).
 *
 * DB row stores `start_local_time` only (`customers.schedule_entries`).
 * `computed_end_local_time` is derived at read time: the start of the next
 * slot in the same `(venue_id, day_of_week)`, or `'23:59:59'` if this is the
 * last slot of its day. PRD §6.7: overlaps are allowed (the UI surfaces them
 * via `scheduleConflictBlockSchema` returned alongside).
 *
 * Wire fields kept snake_case (admin-arch §3.0.0 wire convention). `notes`
 * intentionally omitted — the DDL has no such column today; a follow-up
 * ticket tracks the addition.
 */
/**
 * `HH:MM` or `HH:MM:SS` (Postgres `time` column returns the latter, the FE
 * sends the former). Accepted on input, normalised to `HH:MM:SS` server-side
 * for stable comparison + audit diff.
 */
export const scheduleLocalTimeSchema = z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/, 'Expected HH:MM or HH:MM:SS 24h time');
/**
 * `day_of_week`: 0..6 (Sunday..Saturday — matches `EXTRACT(dow FROM ...)`
 * Postgres convention) OR `null` for "every day" (§17, ADR-005 design).
 */
export const scheduleDayOfWeekSchema = z
    .number()
    .int()
    .min(0)
    .max(6)
    .nullable();
/**
 * Day-only variant used in `copy-day` / `clear-day` / `conflicts-preview`
 * bodies — those operations are scoped to a concrete day and reject `null`.
 */
export const scheduleDayOfWeekStrictSchema = z.number().int().min(0).max(6);
/** Single slot returned by GET / POST / PATCH responses. */
export const scheduleSlotSchema = z
    .object({
    id: uuidSchema,
    venue_id: uuidSchema,
    day_of_week: scheduleDayOfWeekSchema,
    start_local_time: z.string(),
    /**
     * Backend-computed loop-until-next end. `'23:59:59'` for the last slot
     * of a day; FE renders directly — no client-side recomputation.
     */
    computed_end_local_time: z.string(),
    preset_id: uuidSchema,
    /**
     * Best-effort JOIN against `content.presets.name`. Null when the preset
     * row is missing (DDL today has no FK on `preset_id_placeholder` — see
     * follow-ups in the impl report).
     */
    preset_name: z.string().nullable(),
    created_at: isoDateTimeSchema,
    updated_at: isoDateTimeSchema,
})
    .strict();
/**
 * Legacy alias retained for callers that imported the Wave 2.1 BE data-model
 * stub shape. New code MUST consume `scheduleSlotSchema`.
 *
 * @deprecated Use `scheduleSlotSchema`.
 */
export const scheduleEntrySchema = scheduleSlotSchema;
/** POST /admin/venues/:id/schedule/slots body. */
export const scheduleSlotCreateSchema = z
    .object({
    day_of_week: scheduleDayOfWeekSchema,
    start_local_time: scheduleLocalTimeSchema,
    preset_id: uuidSchema,
})
    .strict();
/** PATCH /admin/venues/:id/schedule/slots/:slot_id body. */
export const scheduleSlotPatchSchema = scheduleSlotCreateSchema.partial();
/**
 * Overlap descriptor returned alongside create / patch / conflicts-preview.
 * `overlap_range.from / to` are normalised `HH:MM:SS` strings.
 */
export const scheduleConflictBlockSchema = z
    .object({
    slot_id: uuidSchema,
    overlap_range: z
        .object({ from: z.string(), to: z.string() })
        .strict(),
})
    .strict();
/** Body for POST `/admin/venues/:id/schedule/copy-day`. */
export const scheduleCopyDayRequestSchema = z
    .object({
    source_day_of_week: scheduleDayOfWeekStrictSchema,
    target_day_of_week: scheduleDayOfWeekStrictSchema,
    /**
     * When `false` (default), existing target-day slots are deleted before
     * the source slots are cloned. When `true`, source slots are appended
     * and overlaps are allowed (PRD §6.7).
     */
    merge: z.boolean().default(false),
})
    .strict();
/** Body for POST `/admin/venues/:id/schedule/clear-day`. */
export const scheduleClearDayRequestSchema = z
    .object({
    day_of_week: scheduleDayOfWeekStrictSchema,
})
    .strict();
/** Body for POST `/admin/venues/:id/schedule/conflicts-preview`. */
export const scheduleConflictsPreviewRequestSchema = z
    .object({
    day_of_week: scheduleDayOfWeekSchema,
    start_local_time: scheduleLocalTimeSchema,
    exclude_slot_id: uuidSchema.optional(),
})
    .strict();
/** Optional `?day=mon|tue|...|sun` filter on GET. */
export const scheduleDayQuerySchema = z
    .object({
    day: z
        .enum(['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'])
        .optional(),
})
    .strict();
/** GET /admin/venues/:id/schedule response envelope. */
export const scheduleListResponseSchema = z
    .object({
    entries: z.array(scheduleSlotSchema),
    venue_timezone: z.string(),
    computed_at: isoDateTimeSchema,
})
    .strict();
/** POST/PATCH response envelope (mutation that returns the slot + conflicts). */
export const scheduleMutateResponseSchema = z
    .object({
    entry: scheduleSlotSchema,
    conflicts: z.array(scheduleConflictBlockSchema),
})
    .strict();
/** Conflicts-preview response — no mutation, just the projected overlaps. */
export const scheduleConflictsPreviewResponseSchema = z
    .object({
    conflicts: z.array(scheduleConflictBlockSchema),
})
    .strict();
export const scheduleCopyDayResponseSchema = z
    .object({
    copied_count: z.number().int().nonnegative(),
    deleted_count: z.number().int().nonnegative(),
})
    .strict();
export const scheduleClearDayResponseSchema = z
    .object({
    deleted_count: z.number().int().nonnegative(),
})
    .strict();
/**
 * Map the canonical `?day=mon..sun` value to its Postgres dow integer
 * (Sunday = 0). Exposed for service-layer consumption — keeping the mapping
 * in shared keeps FE and BE in sync.
 */
export const SCHEDULE_DAY_TO_DOW = {
    sun: 0,
    mon: 1,
    tue: 2,
    wed: 3,
    thu: 4,
    fri: 5,
    sat: 6,
};
//# sourceMappingURL=schedule.js.map